/// <amd-module name="Test/Analytics/TestResultsReportView/ControllerViews/TestResultsReportView" />
import { TrendChartView } from "Analytics/Shared/TrendChart/TrendChartView/ControllerViews/TrendChartView";
import { AnalyticsUnavailableMessage } from "Analytics/Shared/Visuals/Common/Components/AnalyticsUnavailableMessage";
import { Fabric } from "OfficeFabric/Fabric";
import * as React from "react";
import { AnnouncementActions } from "Test/Analytics/Common/Actions/AnnouncementActions";
import * as Definitions from "Test/Analytics/Common/Definitions";
import { AnnouncementStore } from "Test/Analytics/Common/Stores/AnnouncementStore";
import * as CommonTypes from "Test/Analytics/Common/Types";
import { ConfigurationUtility } from "Test/Analytics/Common/Utils/ConfigurationUtility";
import * as TcmPerfScenarios from "Test/Analytics/Common/Utils/TCMPerfScenarios";
import * as TcmTelemetryConstants from "Test/Analytics/Common/Utils/TcmTelemetryConstants";
import { publishEvent } from "Test/Analytics/Common/Utils/TelemetryService";
import { TestResultsGridActions } from "Test/Analytics/TestResultsGrid/Actions/TestResultsGridActions";
import { TestResultsGridActionsCreator } from "Test/Analytics/TestResultsGrid/Actions/TestResultsGridActionsCreator";
import { TestResultsTree } from "Test/Analytics/TestResultsGrid/Components/TestResultsTreeGrid";
import { DetailedTestListStore } from "Test/Analytics/TestResultsGrid/Store/TestResultsTreeStore";
import * as Resources from "Test/Analytics/TestResultsReportView/Resources";
import { ITestResultsReportState, TestResultsReportStore } from "Test/Analytics/TestResultsReportView/Stores/TestResultReportStore";
import * as TCMContracts from "Test/common-util/Contracts/Test";
import { FilterValueItem } from "Test/common-util/TestResults.Filtering.Common";
import { ConfigurationToolbarActions } from "Test/ConfigurationToolbar/Actions/ConfigurationToolbarActions";
import { ConfigurationToolbarActionsCreator } from "Test/ConfigurationToolbar/Actions/ConfigurationToolbarActionsCreator";
import { ConfigurationToolbarStore } from "Test/ConfigurationToolbar/Store/ConfigurationToolbarStore";
import { FailedTestsMetricActions } from "Test/FailedTestsMetricView/Actions/FailedTestsMetricActions";
import { FailedTestsMetricActionsCreator } from "Test/FailedTestsMetricView/Actions/FailedTestsMetricActionsCreator";
import { FailedTestsMetricView } from "Test/FailedTestsMetricView/ControllerViews/FailedTestsMetricView";
import { FailedTestsMetricStore } from "Test/FailedTestsMetricView/Stores/FailedTestsMetricStore";
import { FilterMetadataActions } from "Test/MetadataHandler/Actions/FilterMetadataActions";
import { FilterMetadataActionsCreator } from "Test/MetadataHandler/Actions/FilterMetadataActionsCreator";
import { FilterMetadataStore } from "Test/MetadataHandler/Store/FilterMetadataStore";
import { PassRateChartActions } from "Test/PassRateChartView/Actions/PassRateChartActions";
import { PassRateChartActionsCreator } from "Test/PassRateChartView/Actions/PassRateChartActionsCreator";
import { PassRateChartView } from "Test/PassRateChartView/ControllerViews/PassRateChartView";
import { PassRateChartStore } from "Test/PassRateChartView/Stores/PassRateChartStore";
import { TrendChartActions } from "Test/TrendChartView/Actions/TrendChartActions";
import { TrendChartActionsCreator } from "Test/TrendChartView/Actions/TrendChartActionsCreator";
import { TrendChartStore } from "Test/TrendChartView/Stores/TrendChartStore";
import { announce } from "VSS/Core/Util/Accessibility";
import { IVssComponentContext, VssComponent, WrappedComponent } from "VSS/Platform/Layout";
import { IVssPerformanceService } from "VSS/Platform/Performance";
import { Spinner, SpinnerSize } from "VSSUI/Spinner";

export interface ITestResultsReportViewProps extends CommonTypes.IReportComponentProps {
    testReportContext: CommonTypes.ITestReportContext;
}

interface IExtendedTestResultsReportState extends ITestResultsReportState {
    minWidth: number;
}

export class TestResultsReportView extends VssComponent<ITestResultsReportViewProps, IExtendedTestResultsReportState> {
    private _announcementStore: AnnouncementStore;
    private _configurationToolbarStore: ConfigurationToolbarStore;
    private _configurationToolbarActionsCreator: ConfigurationToolbarActionsCreator;
    private _passRateChartStore: PassRateChartStore;
    private _passRateChartActionsCreator: PassRateChartActionsCreator;
    private _failedTestsMetricStore: FailedTestsMetricStore;
    private _failedTestsMetricActionsCreator: FailedTestsMetricActionsCreator;
    private _trendChartStore: TrendChartStore;
    private _trendChartActionsCreator: TrendChartActionsCreator;
    private _detailedListStore: DetailedTestListStore;
    private _detailedListActionCreator: TestResultsGridActionsCreator;
    private _filterMetadataActionCreator: FilterMetadataActionsCreator;
    private _filterMetadataStore: FilterMetadataStore;
    private _store: TestResultsReportStore;
    private _perfService: IVssPerformanceService;

    constructor(props: ITestResultsReportViewProps, context: IVssComponentContext) {
        super(props);

        const reportConfigurationValues: CommonTypes.IReportConfiguration = {
            groupBy: CommonTypes.GroupBy.None,
            trendBy: CommonTypes.TrendBy.Days,
            period: CommonTypes.Period.Days_14,
            outcomes: [CommonTypes.TestOutcome.Failed],
            configuredFilters:
                this.props.testReportContext.contextType === TCMContracts.TestResultsContextType.Build
                    ? {
                          [CommonTypes.Filter.Workflow]: {
                              values: [
                                  new FilterValueItem(
                                      CommonTypes.Workflow.Build.toString(),
                                      new Definitions.WorkflowConfigurationProps().options[CommonTypes.Workflow.Build]
                                  )
                              ]
                          }
                      }
                    : {}
        };
        const announcementActions = new AnnouncementActions();
        this._announcementStore = new AnnouncementStore(announcementActions);
        this._announcementStore.addChangedListener(this._onAnnouncementUpdate);

        const filterMetadataActions = new FilterMetadataActions();
        this._filterMetadataActionCreator = new FilterMetadataActionsCreator(filterMetadataActions, context.pageContext);
        this._filterMetadataStore = new FilterMetadataStore(filterMetadataActions);

        const configurationToolbarActions = new ConfigurationToolbarActions();
        this._configurationToolbarStore = new ConfigurationToolbarStore(configurationToolbarActions, reportConfigurationValues);
        this._configurationToolbarActionsCreator = new ConfigurationToolbarActionsCreator(
            configurationToolbarActions,
            this._filterMetadataActionCreator
        );

        const passRateChartActions = new PassRateChartActions();
        this._passRateChartStore = new PassRateChartStore(passRateChartActions);
        this._passRateChartActionsCreator = new PassRateChartActionsCreator(passRateChartActions, announcementActions, context.pageContext);

        const failedTestsMetricActions = new FailedTestsMetricActions();
        this._failedTestsMetricStore = new FailedTestsMetricStore(failedTestsMetricActions);
        this._failedTestsMetricActionsCreator = new FailedTestsMetricActionsCreator(
            failedTestsMetricActions,
            announcementActions,
            context.pageContext
        );

        const trendChartActions = new TrendChartActions();
        this._trendChartStore = new TrendChartStore(trendChartActions, filterMetadataActions);
        this._trendChartActionsCreator = new TrendChartActionsCreator(trendChartActions, announcementActions, context.pageContext);

        const detailedListActions = new TestResultsGridActions();
        this._detailedListStore = new DetailedTestListStore(
            detailedListActions,
            context.pageContext,
            this.props.testReportContext,
            filterMetadataActions
        );
        this._detailedListActionCreator = new TestResultsGridActionsCreator(detailedListActions, announcementActions, context.pageContext);

        this._perfService = context.pageContext.getService<IVssPerformanceService>("IVssPerformanceService");

        this._store = new TestResultsReportStore(passRateChartActions, failedTestsMetricActions, trendChartActions, detailedListActions);
        this.state = this._store.getState() as IExtendedTestResultsReportState;
    }

    public componentDidMount() {
        super.componentDidMount();
        let workflow = 0;
        if (this.props.testReportContext.contextType === TCMContracts.TestResultsContextType.Build) {
            workflow = CommonTypes.Workflow.Build;
        } else if (this.props.testReportContext.contextType === TCMContracts.TestResultsContextType.Release) {
            workflow = CommonTypes.Workflow.Release;
        }
        const defaultConfValues = {
            groupBy: CommonTypes.GroupBy.None,
            trendBy: CommonTypes.TrendBy.Days,
            period: CommonTypes.Period.Days_14,
            outcomes: [CommonTypes.TestOutcome.Failed],
            workflow: workflow,
            configuredFilters:
                workflow === CommonTypes.Workflow.Build
                    ? {
                          [CommonTypes.Filter.Workflow.toString()]: {
                              values: [new FilterValueItem(CommonTypes.Workflow.Build, CommonTypes.Workflow[CommonTypes.Workflow.Build])]
                          }
                      }
                    : {}
        } as CommonTypes.IReportConfiguration;

        this._store.addChangedListener(this._onStoreUpdate);

        const passRatePromise: Promise<void> = this._passRateChartActionsCreator.updatePassedRateChart(
            this.props.testReportContext,
            defaultConfValues
        );
        const failingTestPromise: Promise<void> = this._failedTestsMetricActionsCreator.updateFailedTestsMetric(
            this.props.testReportContext,
            defaultConfValues
        );
        this._failedTestsMetricActionsCreator.updateFlakyTestsMetric(
            this.props.testReportContext,
            defaultConfValues
        );
        const trendChartPromise: Promise<void> = this._trendChartActionsCreator.updateTrendChart(
            this.props.testReportContext,
            defaultConfValues,
            CommonTypes.Metric.ResultCountAndPassRate,
            CommonTypes.LoadType.PageLoad
        );
        this._detailedListActionCreator.updateConfigurationValues(defaultConfValues);
        this._detailedListActionCreator.updateDetailedTestList(
            this.props.testReportContext,
            defaultConfValues,
            this._detailedListStore.getState().sortedColumn,
            this._detailedListStore.getNextPageToken(undefined),
            true,
            false
        );

        Promise.all([passRatePromise, failingTestPromise, trendChartPromise]).then(
            () => {
                this._perfService.markTimeToInteractive(TcmPerfScenarios.Area, TcmPerfScenarios.TestAX_FailuresReport);
            },
            error => {
                this._perfService.markTimeToInteractive(TcmPerfScenarios.Area, TcmPerfScenarios.TestAX_FailuresReport, undefined, {
                    status: "error"
                });
            }
        );

        const telemetryData: { [key: string]: any } = {
            [TcmTelemetryConstants.Duration]: CommonTypes.Period[defaultConfValues.period].toString()
        };

        publishEvent(
            this.context.pageContext,
            TcmTelemetryConstants.featureTestAX_FailuresReport,
            telemetryData,
            this.props.testReportContext,
            defaultConfValues
        );
    }

    public componentWillUnmount() {
        this._store.dispose();
        this._filterMetadataStore.dispose();
        this._detailedListStore.dispose();
        this._trendChartStore.dispose();
        this._failedTestsMetricStore.dispose();
        this._configurationToolbarStore.dispose();
        this._passRateChartStore.dispose();
        this._announcementStore.removeChangedListener(this._onAnnouncementUpdate);
        super.componentWillUnmount();
    }

    public render(): JSX.Element {
        let body = <div />;
        const areFiltersApplied = ConfigurationUtility.areFiltersApplied(
            this._configurationToolbarStore.getState().reportConfigurationValues.configuredFilters
        );

        if (this.state.viewType === CommonTypes.ViewType.ReportView) {
            body = (
                <div className="new-testresults-analytics-report-view scroll-hidden flex-column flex-grow">
                    {this._renderConfigurationToolbar()}

                    <div className="testresults-analytics-report-view-content flex-column flex-grow">
                        <div
                            className="flex-row flex-start testresults-analytics-report-view-aggregatereports"
                        >
                            <div className="tests-failure-charts flex-column">
                                <PassRateChartView
                                    className="pass-rate-chart-view curved-border-white-background flex-column"
                                    store={this._passRateChartStore}
                                    actionCreator={this._passRateChartActionsCreator}
                                    testReportContext={this.props.testReportContext}
                                />

                                <FailedTestsMetricView
                                    className="failed-tests-metric curved-border-white-background flex-column"
                                    store={this._failedTestsMetricStore}
                                    actionCreator={this._failedTestsMetricActionsCreator}
                                    testReportContext={this.props.testReportContext}
                                />
                            </div>

                            <TrendChartView
                                className="tests-column-chart-view curved-border-white-background flex-grow flex-column"
                                store={this._trendChartStore}
                                actionCreator={this._trendChartActionsCreator}
                                onMetricChanged={this._onMetricChanged}
                            />
                        </div>

                        <div
                            className="test-results-grid-container curved-border-white-background  flex-grow"
                        >
                            <TestResultsTree
                                className="test-results-grid"
                                ariaLabelForGrid={Resources.TestResultsGridAriaLabelGridText}
                                store={this._detailedListStore}
                                actionCreator={this._detailedListActionCreator}
                                testReportContext={this.props.testReportContext}
                                onItemInvoked={this._onListItemInvoked}
                                onSetMinWidth={this._onSetMinWidth}
                            />
                        </div>
                    </div>
                </div>
            );
        } else if (this.state.viewType === CommonTypes.ViewType.TestInsightsReportView) {
            if (this.state.testContext) {
                body = (
                    <WrappedComponent
                        dependencies={["ms.vss-test-analytics-web.analytics-insights-report-view"]}
                        wrappedType="test-insights-report-view"
                        testReportContext={this.props.testReportContext}
                        testContext={this.state.testContext}
                        onBackNavigation={this._onBackNavigation}
                        confValues={this._configurationToolbarStore.getState().reportConfigurationValues}
                        loadingComponent={() => <Spinner className="analytics-card-spinner flex-grow flex-column" size={SpinnerSize.large} />}
                    />
                );
            }
        } else if (this.state.viewType === CommonTypes.ViewType.NoTestDataView) {
            body = (
                <div>
                    {this._renderConfigurationToolbar()}
                    <AnalyticsUnavailableMessage
                        imageName={"testresults-not-found.svg"}
                        message={areFiltersApplied ? Resources.NoResultsMessage : Resources.AnalyticsTestResultsNotFoundMessage}
                        suggestion={areFiltersApplied ? Resources.NoResultsSuggestionMessage : Resources.AnalyticsTestResultsNotFoundSuggestion}
                        ariaDescription={areFiltersApplied ? Resources.NoResultsMessage : Resources.AnalyticsTestResultsNotFoundMessage}
                    />
                    {publishEvent(
                        this.context.pageContext,
                        TcmTelemetryConstants.featureTestAX_NoTestResults,
                        {},
                        this.props.testReportContext,
                        this._configurationToolbarStore.getState().reportConfigurationValues
                    )}
                </div>
            );
        } else if (this.state.viewType === CommonTypes.ViewType.DataNotReadyView) {
            body = (
                <AnalyticsUnavailableMessage
                    imageName={"data-not-ready.svg"}
                    message={Resources.AnalyticsDataNotReadyMessage}
                    suggestion={Resources.AnalyticsDataNotReadySuggestion}
                    ariaDescription={Resources.AnalyticsDataNotReadyMessage}
                />
            );
        } else if (this.state.viewType === CommonTypes.ViewType.ErrorView) {
            body = (
                <AnalyticsUnavailableMessage
                    imageName={"ServiceError.svg"}
                    message={Resources.AnalyticsErrorMessage}
                    suggestion={this.state.errorText}
                    ariaDescription={Resources.AnalyticsErrorMessage}
                />
            );
        }

        return <Fabric className="testresults-analytics-report-fabric flex-grow flex-column scroll-hidden"> {body} </Fabric>;
    }

    private _onSetMinWidth = (minWidth: number) => {
        if (this.state && (!this.state.minWidth || this.state.minWidth !== minWidth)) {
            this.setState({ minWidth: minWidth });
        }
    };

    private _renderConfigurationToolbar = (): JSX.Element => {
        return (
            <WrappedComponent
                dependencies={["ms.vss-test-analytics-web.configuration-toolbar"]}
                wrappedType="configuration-toolbar"
                store={this._configurationToolbarStore}
                actionCreator={this._configurationToolbarActionsCreator}
                testReportContext={this.props.testReportContext}
                onConfigurationChange={this._onConfigurationToolbarChanged}
                isTestInsightsView={false}
                testContext={{}}
                filterMetadataActionCreator={this._filterMetadataActionCreator}
                filterMetadataStore={this._filterMetadataStore}
            />
        );
    };

    private _onListItemInvoked = (item: CommonTypes.IDetailedListItem) => {
        if (item.nodeType === CommonTypes.TreeNodeType.leaf) {
            this.setState({
                viewType: CommonTypes.ViewType.TestInsightsReportView,
                testContext: this._detailedListStore.getTestContext(item)
            });
        }
    };

    private _onBackNavigation = () => {
        this.setState({
            viewType: CommonTypes.ViewType.ReportView,
            testContext: null
        });
    };

    private _onConfigurationToolbarChanged = (changedConfValues: CommonTypes.IReportConfiguration) => {
        this._passRateChartActionsCreator.updateConfigurationValues(changedConfValues);
        this._passRateChartActionsCreator.updatePassedRateChart(this.props.testReportContext, changedConfValues);

        this._failedTestsMetricActionsCreator.updateConfigurationValues(changedConfValues);
        this._failedTestsMetricActionsCreator.updateFailedTestsMetric(this.props.testReportContext, changedConfValues);
        this._failedTestsMetricActionsCreator.updateFlakyTestsMetric(this.props.testReportContext, changedConfValues);

        this._trendChartActionsCreator.updateConfigurationValues(changedConfValues);
        const selectedMetric = this._trendChartStore.getState().metricSelected;
        this._trendChartActionsCreator.updateTrendChart(
            this.props.testReportContext,
            changedConfValues,
            selectedMetric!,
            CommonTypes.LoadType.ReportConfigurationChange
        );

        this._detailedListActionCreator.updateConfigurationValues(changedConfValues);
        this._detailedListActionCreator.updateDetailedTestList(
            this.props.testReportContext,
            changedConfValues,
            this._detailedListStore.getState().sortedColumn,
            this._detailedListStore.getNextPageToken(undefined),
            true,
            true
        );
    };

    private _onMetricChanged = (metricOptionSelected: number) => {
        const confValues = this._configurationToolbarStore.getState().reportConfigurationValues;
        this._trendChartActionsCreator.updateConfigurationValues(confValues);
        this._trendChartActionsCreator.updateTrendChartMetric(metricOptionSelected);
        this._trendChartActionsCreator.updateTrendChart(
            this.props.testReportContext,
            confValues,
            metricOptionSelected,
            CommonTypes.LoadType.ChartMetricChange
        );
    };

    private _onStoreUpdate = () => {
        this.setState(this._store.getState());
    };

    private _onAnnouncementUpdate = () => {
        announce(this._announcementStore.getState().announcementText);
    };
}
